import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResourceError } from "../../utils/ResourceError";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { SaveBar } from "../../utils/SaveBar";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import {
  fetchHseSettings,
  updateHseSettings,
} from "../../../api/hse/settings";
import { HseFieldInfoLabel } from "./HseFieldInfoLabel";
import {
  HSE_MODULE_KEYS,
  HSE_REVIEW_CADENCE_MONTHS,
  emptyHseModuleSettings,
  normalizeHseModuleSettings,
  snapshotHseModuleSettings,
  toHseModuleSettingsPayload,
} from "./hseModuleSettingsUtils";

export function HseModuleSettingsForm() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const rememberUser = (event) => {
    const user = event.target.object;
    if (user?.id) setSelectedUsers((previous) => [...previous.filter((row) => row.id !== user.id), user]);
  };
  const selectedUser = (id) => id ? selectedUsers.find((user) => user.id === id) || { id, displayTitle: `#${id}` } : null;
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [settings, setSettings] = useState(emptyHseModuleSettings());
  const [savedSnapshot, setSavedSnapshot] = useState(
    snapshotHseModuleSettings(emptyHseModuleSettings())
  );

  const applyLoaded = useCallback((data) => {
    setSelectedUsers(data.selected_users || []);
    const next = normalizeHseModuleSettings(data);
    setSettings(next);
    setSavedSnapshot(snapshotHseModuleSettings(next));
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchHseSettings();
      applyLoaded(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [applyLoaded, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasChanges = useMemo(
    () => snapshotHseModuleSettings(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patchSettings = (patch) => {
    setSaveSuccess(false);
    setSaveError(false);
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    try {
      const data = await updateHseSettings(toHseModuleSettingsPayload(settings));
      applyLoaded(data);
      setSaveSuccess(true);
      return true;
    } catch {
      setSaveError(true);
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  if (loadError) return <ResourceError errors={[t("hse.settings.load_failed")]} />;

  return (
    <>
      <fieldset disabled={saving} className="mx-auto min-w-0 max-w-3xl space-y-4 pb-28">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              {t("hse.settings.modules.title")}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.settings.modules.description")}
            </p>
          </div>
          <div className="space-y-2">
            {HSE_MODULE_KEYS.map((key) => (
              <label
                key={key}
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3"
              >
                <input
                  type="checkbox"
                  checked={settings.modules[key] === true}
                  onChange={(event) =>
                    patchSettings({
                      modules: {
                        ...settings.modules,
                        [key]: event.target.checked,
                      },
                    })
                  }
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                />
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    {t(`hse.settings.modules.${key}`)}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t(`hse.settings.modules.${key}_help`)}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <HseFieldInfoLabel
              label="hse.settings.responsible.title"
              infoTitle="hse.settings.responsible.info_title"
              infoBody="hse.settings.responsible.info_body"
            />
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.settings.responsible.description")}
            </p>
          </div>
          <EmployeeSelector
            name="responsible_user_id"
            label="hse.settings.responsible.title"
            values={{ responsible_user_id: settings.responsible_user_id }}
            setFieldValue={(_, value) =>
              patchSettings({ responsible_user_id: value || null })
            }
            selected={selectedUser(settings.responsible_user_id)}
            onChange={rememberUser}
            key={settings.responsible_user_id || "none"}
          />
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-slate-900">
              {t("hse.settings.other_manager.title")}
            </h4>
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.settings.other_manager.description")}
            </p>
            <div className="mt-3">
              <EmployeeSelector
                name="other_manager_user_id"
                label="hse.settings.other_manager.title"
                values={{ other_manager_user_id: settings.other_manager_user_id }}
                setFieldValue={(_, value) =>
                  patchSettings({ other_manager_user_id: value || null })
                }
                selected={selectedUser(settings.other_manager_user_id)}
            onChange={rememberUser}
            key={settings.other_manager_user_id || "none"}
              />
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <HseFieldInfoLabel
              label="hse.settings.cadence.title"
              infoTitle="hse.settings.cadence.info_title"
              infoBody="hse.settings.cadence.info_body"
            />
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.settings.cadence.description")}
            </p>
          </div>
          <select
            value={settings.review_cadence_months}
            onChange={(event) =>
              patchSettings({
                review_cadence_months: parseInt(event.target.value, 10),
              })
            }
            className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          >
            {HSE_REVIEW_CADENCE_MONTHS.map((months) => (
              <option key={months} value={months}>
                {t(`hse.settings.cadence.${months}`)}
              </option>
            ))}
          </select>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <HseFieldInfoLabel
              label="hse.settings.tiltak_defaults.title"
              infoTitle="hse.settings.tiltak_defaults.info_title"
              infoBody="hse.settings.tiltak_defaults.info_body"
            />
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.settings.tiltak_defaults.description")}
            </p>
          </div>
          <label className="block text-sm font-medium text-slate-700">
            {t("hse.settings.tiltak_defaults.due_days")}
            <input
              type="number"
              min={1}
              max={365}
              value={settings.default_tiltak_due_days}
              onChange={(event) =>
                patchSettings({
                  default_tiltak_due_days: parseInt(event.target.value, 10) || 14,
                })
              }
              className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
            />
          </label>
          <label className="mt-4 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
            <input
              type="checkbox"
              checked={settings.tiltak_require_approval === true}
              onChange={(event) =>
                patchSettings({ tiltak_require_approval: event.target.checked })
              }
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
            />
            <div className="text-sm font-medium text-slate-900">
              {t("hse.settings.tiltak_defaults.require_approval")}
            </div>
          </label>
        </section>
      </fieldset>

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saving}
        isSuccess={saveSuccess}
        isError={saveError}
        successMessage={t("hse.settings.saved")}
        errorMessage={t("hse.settings.save_failed")}
        onSave={handleSave}
        onDismiss={() => setSaveError(false)}
        saveLabel={t("save")}
      />
    </>
  );
}
